import { ChevronLeft, Instagram, Linkedin } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { useNavigate, useParams } from "react-router-dom";
import BottomNav from "../components/BottomNav.tsx";
import DotsBackground from "../components/DotsBackground.tsx";
import LeftSidebar from "../components/LeftSidebar.tsx";
import RightSidebar from "../components/RightSidebar.tsx";
import TopProfileBar from "../components/TopProfileBar.tsx";
import {
  createFallbackGuideContent,
  getGuideByUnitAndSection,
  wordBank,
} from "../data/index.ts";
import { getUserData } from "../utils/userStorage.ts";
import { useRandomCharacterImage } from "../hooks/useRandomCharacterImage.ts";

// Example guide data for multiple units/sections

const GuidePage: React.FC = () => {
  const [currentSection, setCurrentSection] = useState("Panduan");
  const [darkMode, setDarkMode] = useState(() => {
    const data = getUserData();
    return data?.meta?.darkMode ?? false;
  });
  const characterImg = useRandomCharacterImage();

  // Responsive breakpoints
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const isTablet = useMediaQuery({ minWidth: 768, maxWidth: 1023 });
  const isDesktop = useMediaQuery({ minWidth: 1024 });

  useEffect(() => {
    // Scroll to top when GuidePage mounts
    window.scrollTo({ top: 0, behavior: "smooth" });
    const handleStorage = (e: StorageEvent) => {
      if (e.key === "galaxy_aksara_user_data") {
        const data = getUserData();
        setDarkMode(data?.meta?.darkMode ?? false);
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);
  // State for hovered word index (for all phrases, only one at a time)
  const [hoveredWordIdx, setHoveredWordIdx] = React.useState<{
    phraseIdx: number;
    wordIdx: string;
  } | null>(null);
  const navigate = useNavigate();
  const params = useParams();
  // Expect params.section and params.unit
  const section = params.section || "1";
  const unit = params.unit || "1";
  // Find guide by section and unit using utility function
  let foundGuide = getGuideByUnitAndSection(unit, section);
  let guideContent = foundGuide
    ? foundGuide.guide_content
    : createFallbackGuideContent(unit, section);

  return (
    <div
      className={`${
        darkMode ? "bg-gray-900" : "bg-gray-100"
      } flex min-h-screen relative`}
    >
      <DotsBackground darkMode={darkMode} />
      {/* Left Sidebar: desktop shows full, tablet shows compact icon-only, mobile hides */}
      {isDesktop && (
        <LeftSidebar
          currentSection={currentSection}
          setCurrentSection={setCurrentSection}
          darkMode={darkMode}
        />
      )}
      {isTablet && !isMobile && (
        <LeftSidebar
          currentSection={currentSection}
          setCurrentSection={setCurrentSection}
          darkMode={darkMode}
          compact
        />
      )}
      <div className="flex-1 flex">
        {/* Top Profile Bar (mobile/tablet only) */}
        {isMobile && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              zIndex: 30,
            }}
          >
            <TopProfileBar darkMode={darkMode}>
              {/* Only show profile section from RightSidebar */}
              <RightSidebar darkMode={darkMode} profileOnly />
            </TopProfileBar>
          </div>
        )}
        <div
          className="flex-1 relative z-10 bg-transparent pt-0 md:pt-0"
          style={isMobile ? { paddingTop: 64, paddingBottom: 64 } : {}}
        >
          {/* Header */}
          <div
            className={`w-full max-w-2xl mx-auto ${
              isMobile || isTablet ? "pt-6 pb-1 px-10" : "pt-12 pb-2 px-2"
            }`}
          >
            <button
              className={`flex items-center font-bold text-lg mb-2 ${
                darkMode ? "text-gray-100" : "text-gray-800"
              }`}
              style={{ fontFamily: "Feather Bold" }}
              onClick={() => navigate(-1)}
            >
              <ChevronLeft className="w-6 h-6 mr-1" /> Kembali
            </button>
            <hr
              className={`${
                darkMode ? "border-gray-700" : "border-gray-300"
              } mb-4`}
            />
            <div
              className={`flex items-center ${
                isMobile || isTablet ? "gap-2 mb-2" : "gap-6 mb-4"
              }`}
            >
              <div
                className={
                  isMobile || isTablet
                    ? "w-16 h-16 flex items-center justify-center"
                    : "w-32 h-32 flex items-center justify-center"
                }
              >
                <img
                  src={characterImg}
                  alt="character illustration"
                  className={
                    isMobile || isTablet
                      ? "w-16 h-16 object-contain"
                      : "w-32 h-32 object-contain"
                  }
                />
              </div>
              <div>
                <div
                  className={`${
                    isMobile || isTablet ? "text-base mb-0.5" : "text-2xl mb-1"
                  } font-bold ${darkMode ? "text-gray-100" : "text-gray-800"}`}
                  style={{ fontFamily: "Feather Bold" }}
                >
                  {guideContent.title}
                </div>
                <div
                  className={`${darkMode ? "text-gray-300" : "text-gray-800"} ${
                    isMobile || isTablet ? "text-sm" : "text-base"
                  } font-medium`}
                  style={{ fontFamily: "DIN Next Rounded" }}
                >
                  {guideContent.description}
                </div>
              </div>
            </div>
            <hr
              className={`${
                darkMode ? "border-gray-700" : "border-gray-300"
              } mt-4 mb-2`}
            />
          </div>
          {/* Key Phrase Section */}
          <div className="w-full max-w-2xl mx-auto px-2">
            <div
              className={`font-bold text-sm mb-1 ${
                isMobile || isTablet ? "px-10" : "px-2"
              } ${darkMode ? "text-gray-200" : "text-gray-800"}`}
              style={{ fontFamily: "Feather Bold" }}
            >
              FRASA KUNCI
            </div>
            <div
              className={`${
                isMobile || isTablet
                  ? "text-base mb-3 px-10"
                  : "text-xl mb-6 px-2"
              } font-bold ${darkMode ? "text-gray-100" : "text-gray-900"}`}
              style={{ fontFamily: "Feather Bold" }}
            >
              {guideContent.keyPhraseTitle}
            </div>
            <div className="space-y-1 pb-10">
              {guideContent.phrases.map((phrase: any, idx: number) => {
                // phrase_sentences is now an array of objects: { phrase_word: [wordBank ids] }
                // Each phrase_word group is rendered with spacing
                const phraseGroups = phrase.phrase_sentences;
                // Flatten all words for full translation
                const allWords = phraseGroups.flatMap((group: any) =>
                  group.phrase_word
                    .map((wid: number) => wordBank.find((w) => w.id === wid))
                    .filter(Boolean)
                );
                return (
                  <div
                    key={idx}
                    className={
                      isMobile || isTablet ? "py-1 px-10" : "py-2 px-2"
                    }
                  >
                    <div
                      className={`rounded-xl shadow ${
                        isMobile || isTablet
                          ? "px-2 py-2 mb-1"
                          : "px-5 py-4 mb-2"
                      } border ${
                        darkMode
                          ? "bg-gray-800 border-gray-700"
                          : "bg-white border-gray-200"
                      }`}
                    >
                      <div
                        className={`mb-1 flex flex-wrap ${
                          isMobile || isTablet ? "gap-2" : "gap-4"
                        }`}
                      >
                        {phraseGroups.map((group: any, groupIdx: number) => (
                          <div key={groupIdx} className="flex gap-2">
                            {group.phrase_word.map(
                              (wid: number, wIdx: number) => {
                                const word = wordBank.find((w) => w.id === wid);
                                if (!word) return null;
                                // For hover, use phraseIdx and a composite wordIdx
                                const compositeIdx = `${groupIdx}-${wIdx}`;
                                return (
                                  <span
                                    key={wIdx}
                                    className="flex flex-col items-center cursor-pointer relative"
                                    onMouseEnter={() =>
                                      setHoveredWordIdx({
                                        phraseIdx: idx,
                                        wordIdx: compositeIdx,
                                      })
                                    }
                                    onMouseLeave={() => setHoveredWordIdx(null)}
                                  >
                                    <span className="flex gap-1">
                                      {word.syllable_mapping &&
                                      word.syllable_mapping.length > 0
                                        ? word.syllable_mapping.map(
                                            (syll, sIdx) => (
                                              <span
                                                key={sIdx}
                                                className="flex flex-col items-center"
                                              >
                                                <span
                                                  className={`font-semibold leading-none ${
                                                    isMobile || isTablet
                                                      ? "text-[10px]"
                                                      : "text-xs"
                                                  } ${
                                                    darkMode
                                                      ? "text-gray-400"
                                                      : "text-gray-500"
                                                  }`}
                                                  style={{
                                                    fontFamily:
                                                      "DIN Next Rounded",
                                                  }}
                                                >
                                                  {syll.latin}
                                                </span>
                                                <span
                                                  className={`font-bold tracking-wide border-b-2 border-dashed ${
                                                    isMobile || isTablet
                                                      ? "text-lg"
                                                      : "text-2xl"
                                                  } ${
                                                    darkMode
                                                      ? "text-gray-100 border-gray-500"
                                                      : "text-gray-900 border-gray-400"
                                                  }`}
                                                  style={{
                                                    fontFamily:
                                                      "Noto Sans Batak, serif",
                                                  }}
                                                >
                                                  {syll.batak_char}
                                                </span>
                                              </span>
                                            )
                                          )
                                        : word.batak
                                            .split("")
                                            .map((char, cIdx) => (
                                              <span
                                                key={cIdx}
                                                className="flex flex-col items-center"
                                              >
                                                <span
                                                  className={`font-semibold leading-none ${
                                                    isMobile || isTablet
                                                      ? "text-[10px]"
                                                      : "text-xs"
                                                  } ${
                                                    darkMode
                                                      ? "text-gray-400"
                                                      : "text-gray-500"
                                                  }`}
                                                  style={{
                                                    fontFamily:
                                                      "DIN Next Rounded",
                                                  }}
                                                >
                                                  {word.latin[cIdx] || char}
                                                </span>
                                                <span
                                                  className={`font-bold ${
                                                    isMobile || isTablet
                                                      ? "text-lg"
                                                      : "text-2xl"
                                                  } ${
                                                    darkMode
                                                      ? "text-gray-100"
                                                      : "text-gray-800"
                                                  }`}
                                                  style={{
                                                    fontFamily:
                                                      "Noto Sans Batak",
                                                  }}
                                                >
                                                  {char}
                                                </span>
                                              </span>
                                            ))}
                                    </span>
                                    {/* Floating popup for Indonesian translation on hover, styled like QuestionBubble */}
                                    {hoveredWordIdx &&
                                      hoveredWordIdx.phraseIdx === idx &&
                                      hoveredWordIdx.wordIdx ===
                                        compositeIdx && (
                                        <span
                                          className={`absolute left-1/2 top-full -translate-x-1/2 rounded-md px-3 py-1 text-xs shadow z-10 whitespace-nowrap mt-2 border ${
                                            darkMode
                                              ? "bg-gray-900 border-gray-600 text-gray-100"
                                              : "bg-white border-gray-300 text-gray-800"
                                          }`}
                                          style={{
                                            fontFamily: "DIN Next Rounded",
                                          }}
                                        >
                                          {word.indonesia}
                                        </span>
                                      )}
                                  </span>
                                );
                              }
                            )}
                          </div>
                        ))}
                      </div>
                      <div
                        className={`text-sky-500 font-medium ${
                          isMobile || isTablet ? "text-sm" : "text-base"
                        }`}
                        style={{ fontFamily: "DIN Next Rounded" }}
                      >
                        {allWords.map((w) => w.indonesia).join(" ")}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          {isMobile && (
            <footer className="w-full pt-2 md:pt-2 pb-12 md:pb-10 bg-transparent flex flex-col items-center gap-2 z-10 relative">
              <div
                className="text-gray-500 text-sm font-semibold"
                style={{ fontFamily: "DIN Next Rounded" }}
              >
                &copy; 2025 Galaxy Developer
              </div>
              <div className="flex gap-4 mt-1">
                <a
                  href="https://www.instagram.com/galaxy.developer/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-gray-500 hover:text-pink-500 transition font-semibold text-sm"
                >
                  <Instagram className="w-5 h-5" />
                </a>
                <a
                  href="https://www.linkedin.com/company/galaxydeveloper"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-gray-500 hover:text-blue-700 transition font-semibold text-sm"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
              </div>
            </footer>
          )}
        </div>
        {/* Right Sidebar (desktop and tablet only) */}
        {(isDesktop || isTablet) && <RightSidebar darkMode={darkMode} />}
      </div>
      {/* Bottom Nav (mobile only) */}
      {isMobile && (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            width: "100vw",
            zIndex: 30,
          }}
        >
          <BottomNav
            currentSection={currentSection}
            setCurrentSection={setCurrentSection}
            darkMode={darkMode}
          />
        </div>
      )}
    </div>
  );
};

export default GuidePage;
